<template>
    <!-- eslint-disable vuejs-accessibility/label-has-for -->
    <!-- we have for, but eslint does not understand it, because we use <component :is="..."> -->
    <label
        :class="[
            $attrs.class,
            $style.rootLabel,
            $style[size],
            {
                [$style.disabled]: disabled,
                [$style.invalid]: isInvalid,
                [$style.filled]: !!inputValue,
                [$style.withVisibleLabel]: !withHiddenLabel,
            },
        ]"
        :for="id"
    >
        <template v-if="labelText">
            <span
                v-if="!withHiddenLabel"
                :class="$style.labelText"
                aria-hidden="true"
            >
                {{ labelText }}
            </span>
            <span :class="[$style.labelText, $style.animatedLabelText, { [$style.invisible]: withHiddenLabel }]">
                {{ labelText }}
            </span>
        </template>
        <Icon
            v-if="prefixIcon"
            :name="prefixIcon"
            :class="$style.prefix_icon"
        />

        <component
            v-bind="{ ...$attrs, class: undefined }"
            :is="is"
            :id="id"
            :class="$style.input"
            :disabled="disabled"
            :type="props.type"
            :value="inputValue"
            @input="onInput($event as UIChangeEvent<HTMLInputElement>)"
        />
    </label>
</template>

<script lang="ts" setup generic="T extends InputTypeHTMLAttribute | undefined">
import { type InputHTMLAttributes, type InputTypeHTMLAttribute, computed, ref } from 'vue'

import type { IconNameType } from '../Icon'
import Icon from '../Icon/Icon.vue'

type ValueType = T extends 'number' ? number : string

interface TextInputProps extends /* @vue-ignore */ InputHTMLAttributes {
    is?: 'input' | 'textarea'
    id: string
    labelText?: string
    modelValue?: ValueType
    disabled?: boolean
    withHiddenLabel?: boolean
    value?: ValueType
    prefixIcon?: IconNameType
    size?: 'l' | 'm'
    type?: T
}
const props = withDefaults(defineProps<TextInputProps>(), {
    size: 'm',
    is: 'input',
})

const withHiddenLabel = computed(() => !props.labelText || props.withHiddenLabel)
const isInvalid = ref(false)

const emit = defineEmits<{ input: [e: UIChangeEvent<HTMLInputElement>]; 'update:modelValue': [value: ValueType] }>()

const inputValue = computed(() => props.modelValue ?? props.value)

const shouldConvertToNumber = computed(() => props.type === 'number' || typeof inputValue.value === 'number')

const convertToNumber = (value: string) => (Number.isFinite(+value) ? +value : 0)

const onInput = (e: UIChangeEvent<HTMLInputElement>) => {
    isInvalid.value = !e.target.validity.valid
    const newValue = shouldConvertToNumber.value ? convertToNumber(e.target.value) : e.target.value
    emit('update:modelValue', newValue as ValueType)
    emit('input', e)
}
</script>

<style module>
/* ROOT STYLES AND DEFINITIONS */

.rootLabel * {
    box-sizing: border-box;
    font-family: var(--font-family);
    font-weight: var(--font-weight-default);
}

.rootLabel {
    --input-scrollbar-thumb-color: var(--carbon-500);
    --input-text-color: var(--white);
    --label-text-color: var(--white-60);
    --placeholder-text-color: var(--white-60);
    --input-background-color: var(--carbon-400);
    --input-side-padding: var(--gap-12);
    --input-border-color: var(--input-background-color);

    --border-animation-duration: 0.1s;
    --background-animation-duration: 0.15s;
    --label-animation-duration: 0.15s;
    --prefix-icon-width: 24px;
    --prefix-icon-height: 24px;
    --input-inside-gap: 4px;

    box-sizing: border-box;
    position: relative;
    cursor: text;
    z-index: 0;
    display: flex;
    flex-direction: column;
    gap: var(--input-to-label-gap);

    /* vertical padding is on input itself, when there is no label */
    padding: 0 var(--input-side-padding);
    border-radius: var(--input-border-radius);
    /* animated border background */
    background: linear-gradient(to right, var(--input-border-color), var(--input-border-color)) center center / 100%
        100% no-repeat;
}

/* before is used for main background */
.rootLabel::before {
    pointer-events: none;
    border-radius: var(--input-border-radius);
    content: '';
    position: absolute;
    /* 1px gives us a way to see the animated border */
    top: 1px;
    left: 1px;
    bottom: 1px;
    right: 1px;
    transition: background-color var(--background-animation-duration) ease-in-out;
    background: var(--input-background-color);
}

.rootLabel.withVisibleLabel {
    padding: var(--input-vertical-padding) var(--input-side-padding);
}

/* SIZES */

.rootLabel.m {
    --input-to-label-gap: var(--gap-4);

    /* exactly pixel perfect with design */
    --input-vertical-padding: 11px;
    --input-vertical-padding-large: 20px;

    /* line-height === font-size by design (important for animations + pixel perfect in requirements) */
    --input-font-size: var(--font-size-paragraph-m);
    --input-line-height: var(--font-size-paragraph-m);
    --input-letter-spacing: var(--letter-spacing-paragraph-m);

    --input-border-radius: var(--radius-12);

    --label-font-size: var(--font-size-label-s);
    --label-line-height: var(--line-height-label);
}

/* size L is just an example of extending component to other sizes. Please, check before use */
.rootLabel.l {
    --input-to-label-gap: var(--gap-4);

    --input-vertical-padding: 14px;
    /* exactly pixel perfect with design */
    --input-vertical-padding-large: 23px;

    /* line-height === font-size by design (important for animations + pixel perfect in requirements) */
    --input-font-size: var(--font-size-paragraph-l);
    --input-line-height: var(--font-size-paragraph-l);
    --input-letter-spacing: var(--letter-spacing-paragraph-l);

    --input-border-radius: var(--radius-16);

    --label-font-size: var(--font-size-label-s);
    --label-line-height: var(--line-height-label);
}

/* BASIC ELEMENTS */

.labelText {
    opacity: 0;
    visibility: hidden;
    font-size: var(--label-font-size);
    line-height: var(--label-line-height);
}

.rootLabel textarea {
    min-height: 72px;
}

.animatedLabelText {
    visibility: visible;
    opacity: 1;
    color: var(--label-text-color);
    transition:
        transform var(--label-animation-duration),
        font-size var(--label-animation-duration);
    position: absolute;
    top: 0;
    font-size: var(--input-font-size);
    line-height: var(--input-line-height);
    letter-spacing: var(--input-letter-spacing);

    transform: translate3d(0, var(--input-vertical-padding-large), 0);
}

.animatedLabelText.invisible {
    opacity: 0;
}

.input {
    color: var(--input-text-color);
    caret-color: var(--purple-400);
    border: none;
    position: relative;
    z-index: 1;
    padding: var(--input-vertical-padding-large) 0;
    font-size: var(--input-font-size);
    letter-spacing: var(--input-letter-spacing);
    height: var(--input-line-height);
    box-sizing: content-box;
    scrollbar-color: var(--input-scrollbar-thumb-color) transparent;
}

.input:-webkit-autofill,
.input:-webkit-autofill:hover,
.input:-webkit-autofill:focus,
.input:-webkit-autofill:active,
.input:-webkit-autofill::first-line {
    /* remove ugly autofill background color and styles */
    border: none;
    -webkit-background-clip: text;
    -webkit-box-shadow: inherit;
    -webkit-text-fill-color: var(--input-text-color);
    transition: background-color 9999s ease-in-out 0s;
    color: var(--input-text-color);
}

.input::placeholder {
    color: var(--placeholder-text-color);
}

.input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
}

.rootLabel.withVisibleLabel .input::placeholder {
    opacity: 0;
}

.rootLabel.withVisibleLabel .input {
    /* padding is on the root label when labels are visible */
    padding: 0;
}

input[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
    display: none;
    height: 24px;
    width: 24px;
    cursor: pointer;

    background: url('../TextInput/images/close.svg') no-repeat center center;
    background-size: contain;
}

input[type='search']:focus::-webkit-search-cancel-button {
    display: block;
}

/* STATES */

.rootLabel.disabled {
    cursor: not-allowed;
    --input-border-color: var(--carbon-400);
    --input-background-color: var(--carbon-100);

    --input-text-color: var(--white-40);
    --label-text-color: var(--white-40);
}

.rootLabel.disabled .input {
    cursor: not-allowed;
}

.rootLabel:hover:not(.disabled) {
    --input-border-color: var(--carbon-500);
    animation: animateBorder var(--border-animation-duration) ease-out forwards;
}

.rootLabel:focus-within:not(.disabled) {
    --input-border-color: var(--carbon-500);
    --input-background-color: var(--carbon-100);
    animation: animateBorder var(--border-animation-duration) ease-out forwards;
}

.rootLabel:focus-within:not(.disabled) .animatedLabelText,
.rootLabel.filled .animatedLabelText {
    font-size: var(--label-font-size);
    line-height: var(--label-line-height);

    transform: translate3d(0, var(--input-vertical-padding), 0);
}

.rootLabel.invalid:not(:focus-within) {
    animation: animateBorder var(--border-animation-duration) ease-out forwards;
    --input-border-color: var(--rose-400);
    --input-background-color: var(--carbon-100);
}

.rootLabel:has(.prefix_icon) .input {
    padding-left: calc(var(--prefix-icon-width) + var(--input-inside-gap));
}

.prefix_icon {
    z-index: 1;
    color: var(--white-80);
    position: absolute;
    width: var(--prefix-icon-width);
    height: var(--prefix-icon-height);
    top: calc(var(--input-side-padding) - 2px);
    left: calc(var(--input-side-padding) - 2px);
}

/* ANIMATIONS */

@keyframes animateBorder {
    0% {
        background-size: 0 100%;
    }

    25% {
        background-size: 10% 100%;
    }

    25% {
        background-size: 90% 100%;
    }

    100% {
        background-size: 100% 100%;
    }
}
</style>
